import { Check } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useInvoiceQuery } from '../../../../api/gql';
import { BottomAppBar } from '../../../../components/BottomAppBar';
import { PageHeader } from '../../../../components/PageHeader';
import { PageContent, PageLayout } from '../../../../layout';
import { InvoiceManagementInvoiceFormType } from '../../../../modules/forms/invoice/types';
import { InvoiceManagementInvoiceFormModule } from '../../../../modules/invoice-management/InvoiceManagementInvoiceFormModule';
import { useConfirmInvoiceManagementInvoiceForm } from '../../../../modules/invoice-management/useConfirmInvoiceManagementInvoiceForm';
import { notify } from '../../../../utils/notification';
import { fromCentesimal } from '../../../../utils/transform';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';

export const InvoiceManagementCopyInvoicePage = () => {
  const { t } = useTranslation('page-invoice-management');
  const { safeNavigate } = useSafeNavigation();
  const { invoiceId } = useParams();

  const { data: invoiceData } = useInvoiceQuery({ id: invoiceId as string });

  const {
    onFormSubmit,
    state: { isReady, isLoading },
  } = useConfirmInvoiceManagementInvoiceForm({
    onSuccess: (id) => safeNavigate(`/invoice-management/invoices/${id}`),
    onError: () => {
      notify.negative({
        title: t('error.mutation.title'),
        message: t('error.mutation.text'),
      });
    },
    onValidationError: () => {
      notify.negative({
        title: t('error.validation.title'),
        message: t('error.validation.text'),
      });
    },
  });

  const handleFormSubmit = async (data: InvoiceManagementInvoiceFormType) => {
    try {
      onFormSubmit(data);
    } catch (e) {
      console.error(e);
      notify.negative({
        title: t('error.mutation.title'),
        message: t('error.mutation.text'),
      });
    }
  };

  return (
    <PageLayout>
      <PageHeader title={t('newInvoice')} backRouteTo={-1} />
      <PageContent>
        {invoiceData?.invoice ? (
          <InvoiceManagementInvoiceFormModule
            invoiceFormProps={{
              formId: 'invoice-form',
              onSubmit: handleFormSubmit,
              defaultValidated: false,
              defaultValues: {
                ...invoiceData?.invoice,
                amount: fromCentesimal(invoiceData.invoice.amount ?? 0),
                amountWithoutVat: invoiceData.invoice.amountWithoutVat
                  ? fromCentesimal(invoiceData.invoice.amountWithoutVat)
                  : undefined,
                amountVat: invoiceData.invoice.amountVat ? fromCentesimal(invoiceData.invoice.amountVat) : undefined,
                currency: invoiceData.invoice.currency?.toLowerCase(),
                items:
                  invoiceData.invoice.items.map((item) => ({
                    ...item,
                    priceSum: fromCentesimal(item.priceSum),
                    pricePerUnit: fromCentesimal(item.pricePerUnit),
                    vatRate: item.vatRate ?? undefined,
                    priceWithoutVat: item.priceWithoutVat ? fromCentesimal(item.priceWithoutVat) : undefined,
                  })) ?? [],
              },
            }}
          />
        ) : null}
      </PageContent>
      <BottomAppBar stackProps={{ justifyContent: 'flex-end' }}>
        <LoadingButton
          loading={isLoading}
          disabled={!isReady}
          type={'submit'}
          form={'invoice-form'}
          variant={'contained'}
          size={'large'}
          startIcon={<Check />}
        >
          {t('action.confirm')}
        </LoadingButton>
      </BottomAppBar>
    </PageLayout>
  );
};
